import { ManagedServiceId } from '@axinom/mosaic-id-utils';
import {
  type ActionData,
  Details,
  type DetailsProps,
  Divider,
  type FormActionData,
  GenericField,
  IconName,
  InfoPanel,
  List,
  Paragraph,
  Section,
  SingleLineTextField,
  type StationMessage,
  formatDateTime,
  getFormDiff,
} from '@axinom/mosaic-ui';
import { Field } from 'formik';
import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { ensureError } from '../../../../common/errors/ensure-error';
import { config } from '../../../../config';
import {
  ReDeploymentServiceDocument,
  type ReDeploymentServiceMutation,
  type ReDeploymentServiceMutationVariables,
  ResourceStatus,
  ServiceDeploymentDetailsDocument,
  type ServiceDeploymentDetailsQuery,
  type ServiceDeploymentDetailsQueryVariables,
  UnDeploymentServiceDocument,
  type UnDeploymentServiceMutation,
  type UnDeploymentServiceMutationVariables,
  UpdateDeploymentServiceDocument,
  type UpdateDeploymentServiceMutation,
  type UpdateDeploymentServiceMutationVariables,
} from '../../../../generated/hosting-service';
import type { BreadcrumbResolver } from '../../../../models/breadcrumb';
import { environmentApolloClient } from '../../../environmentApolloClient';
import { LabeledIconField } from '../common/components/LabeledIconField/LabeledIconField';
import { getServiceDeploymentStatusIconLabel } from '../common/utils';
import type { DeploymentDetails } from './CustomizableServiceDeploymentCreate.types';
import { RowSelectField } from './RowSelectField/RowSelectField';
import { ActiveDeploymentRenderer } from './ServiceDeploymentStatusRenderer';
interface ApiUrl {
  urlName: string;
}

export const CustomizableServiceDeploymentDetails: React.FC = () => {
  const [error, setError] = useState<unknown>();
  const [loading, setLoading] = useState<boolean>(false);
  const [initialData, setInitialData] = useState<DeploymentDetails>();
  const [operationPendingMessage, setOperationPendingMessage] =
    useState<StationMessage>();
  const [deploymentError, setDeploymentError] = useState<StationMessage>();
  const { envId, serviceDeploymentId, customizableServiceId } = useParams<{
    envId: string;
    serviceDeploymentId: string;
    customizableServiceId: string;
  }>();

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const envApolloClient = environmentApolloClient(
          envId,
          new URL(`/graphql`, config.hostingServiceAdminBaseUrl).toString(),
        );
        const deploymentDetails = await envApolloClient.query<
          ServiceDeploymentDetailsQuery,
          ServiceDeploymentDetailsQueryVariables
        >({
          query: ServiceDeploymentDetailsDocument,
          variables: {
            id: serviceDeploymentId,
          },
          fetchPolicy: 'no-cache',
        });
        const deployment =
          deploymentDetails?.data?.serviceDeployment ?? undefined;

        if (deployment) {
          const artifactsList: {
            name: string;
            packageVersion: string;
            id: number;
          }[] = [];
          if (deployment?.servicePiletArtifacts) {
            deployment?.servicePiletArtifacts?.split(',')?.map((art, index) => {
              const item = {
                id: index,
                name: art.split('@')[0],
                packageVersion: art.split('@')[1],
              };
              artifactsList.push(item);
            });
          } else {
            const item = {
              id: 0,
              name: '',
              packageVersion: '',
            };
            artifactsList.push(item);
          }
          setInitialData({
            ...deployment,
            containerImage: [
              {
                id: 1,
                containerImageVersion: `${deployment?.serviceDefinition?.repositoryName}:${deployment?.containerImageVersion}`,
              },
            ],
            serviceDeploymentManifest: [
              deploymentDetails?.data?.serviceDeployment
                ?.serviceDeploymentManifest,
            ],
            servicePiletArtifacts: artifactsList,
          });
          if (deployment.status === ResourceStatus.CreatePending) {
            setOperationPendingMessage({
              title:
                'The service is now being deployed, and this operation may take some minutes to complete. Please come back to this station or refresh to check if the deployment is successful.',
              type: 'info',
            });
          } else if (deployment.status === ResourceStatus.DeletePending) {
            setOperationPendingMessage({
              title:
                'The service is now being undeployed, and this operation may take some minutes to complete. Please come back to this station or refresh to check if the undeployment is successful.',
              type: 'info',
            });
          } else {
            setOperationPendingMessage(undefined);
          }
          if (
            deployment.status === ResourceStatus.CreateError ||
            deployment.status === ResourceStatus.DeleteError
          ) {
            // TODO: Update message in future to view deployment-logs for more details
            setDeploymentError({
              type: 'warning',
              title: `Service deployment has failed with the following error (check deployment logs for details): ${deployment.errorMessage}`,
            });
          }
        } else {
          setInitialData(undefined);
          throw Error('The Customizable Service does not exist');
        }
      } catch (e) {
        const error = ensureError(e);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [envId, serviceDeploymentId]);

  const onSubmit = useCallback(
    async (
      formData: DeploymentDetails,
      initialData: DetailsProps<DeploymentDetails>['initialData'],
    ): Promise<void> => {
      if (initialData.loading) {
        return;
      }

      const envApolloClient = environmentApolloClient(
        envId,
        new URL(`/graphql`, config.hostingServiceAdminBaseUrl).toString(),
      );

      await envApolloClient?.mutate<
        UpdateDeploymentServiceMutation,
        UpdateDeploymentServiceMutationVariables
      >({
        mutation: UpdateDeploymentServiceDocument,
        variables: {
          input: {
            id: serviceDeploymentId,
            patch: getFormDiff(formData, initialData.data),
          },
        },
      });
    },
    [envId, serviceDeploymentId],
  );
  const generateAPIUrls = (): ApiUrl[] => {
    const urlArray: ApiUrl[] = [];
    initialData?.dnsRecords?.nodes?.map((dnsRecord) => {
      const url = {
        urlName:
          `https://${dnsRecord.fqdn}` +
          (initialData.serviceDefinition?.ownedByAxinom ? `/graphiql` : ''),
      };
      urlArray.push(url);
    });
    return urlArray;
  };

  const Form: React.FC = () => {
    const { icon, label } = getServiceDeploymentStatusIconLabel(
      initialData?.status,
    );
    const useGenerateInlineMenuActions: (data: ApiUrl) => ActionData[] = (
      data,
    ) => {
      return [
        {
          label: 'Open URL',
          path: { pathname: data?.urlName },
          openInNewTab: true,
        },
        {
          label: 'Copy URL',
          icon: IconName.Copy,
          onActionSelected: () => {
            try {
              // copy url to clipboard
              navigator.clipboard.writeText(data?.urlName ?? '');
            } catch (_error) {
              // eslint-disable-next-line no-console
              console.warn('Failed to copy to clipboard');
            }
          },
        },
      ];
    };
    return (
      <>
        <Field
          name="name"
          label="Name"
          as={SingleLineTextField}
          defaultValue={``}
        />
        <LabeledIconField
          name="status"
          label="Status"
          icon={icon}
          value={label}
        />
        <Divider />
        <Field
          name="containerImage"
          label="Container Image"
          type="ContainerImage"
          max={-1}
          as={RowSelectField}
          disabled={true}
        />
        <Field
          name="servicePiletArtifacts"
          label="Pilet Artifact(s)"
          type="ServicePiletArtifacts"
          max={0}
          as={RowSelectField}
          disabled={true}
        />
        <Field
          name="serviceDeploymentManifest"
          label="Deployment Manifest"
          type="ServiceDeploymentManifest"
          max={-1}
          as={RowSelectField}
          disabled={true}
        />
        <Divider />
        {initialData?.active &&
          initialData?.status === ResourceStatus.Ready && (
            <>
              <GenericField label="API URL(s)" name="apiUrls">
                {initialData?.dnsRecords.nodes.length > 0 ? (
                  <List
                    columns={[
                      {
                        propertyName: 'urlName',
                        label: 'URL',
                        sortable: false,
                      },
                    ]}
                    data={generateAPIUrls()}
                    inlineMenuActions={useGenerateInlineMenuActions}
                    showActionButton={false}
                  />
                ) : (
                  <p>{'No URL(s) defined'}</p>
                )}
              </GenericField>
            </>
          )}
      </>
    );
  };

  const Panel: React.FC = () => {
    return (
      <InfoPanel>
        <Section>
          <Paragraph title={'ID'}>{initialData?.id}</Paragraph>
          <Paragraph title={'Service Definition ID'}>
            {initialData?.serviceDefinitionId}
          </Paragraph>
          <Paragraph title={'Service ID'}>
            {initialData?.serviceDefinition?.serviceId}
          </Paragraph>
          <Paragraph title={'Service Name'}>
            {initialData?.serviceDefinition?.name}
          </Paragraph>
          <Paragraph title={'Active Deployment'}>
            {ActiveDeploymentRenderer(initialData?.active)}
          </Paragraph>
          <Paragraph title={'Created'}>
            {formatDateTime(initialData?.createdDate)} by{' '}
            {initialData?.createdUser}
          </Paragraph>
          <Paragraph title={'Last Modified'}>
            {formatDateTime(initialData?.updatedDate)} by{' '}
            {initialData?.updatedUser}
          </Paragraph>
        </Section>
        <Section>
          <Paragraph title={'Tenant ID'}>{initialData?.tenantId}</Paragraph>
          <Paragraph title={'Environment ID'}>{envId}</Paragraph>
        </Section>
      </InfoPanel>
    );
  };

  const { actions } = useActions(
    envId,
    serviceDeploymentId,
    customizableServiceId,
    initialData,
  );

  return (
    <>
      <Details<DeploymentDetails>
        defaultTitle="Deployments Details"
        actions={initialData ? actions : []}
        initialData={{
          data: initialData,
          loading,
          error: error ?? undefined,
        }}
        infoPanel={initialData ? <Panel /> : undefined}
        saveData={onSubmit}
        stationMessage={operationPendingMessage ?? deploymentError}
      >
        <Form />
      </Details>
    </>
  );
};

function useActions(
  envId: string,
  serviceDeploymentId: string,
  customizableServiceId: string,
  initialData: DeploymentDetails | undefined,
): {
  readonly actions: FormActionData<DeploymentDetails>[];
} {
  const envApolloClient = environmentApolloClient(
    envId,
    new URL(`/graphql`, config.hostingServiceAdminBaseUrl).toString(),
  );
  const history = useHistory();
  const actions: FormActionData<DeploymentDetails>[] = [
    {
      label: 'Deployment Logs',
      path: `/env/${envId}/services/${ManagedServiceId.HOSTING_SERVICE}/config/customizable-services/${customizableServiceId}/deployments/${serviceDeploymentId}/logs`,
    },
  ];

  if (initialData?.active === true) {
    actions.push(
      {
        label: 'Redeploy',
        confirmationMode: 'Advanced',
        confirmationConfig: {
          body: `This will create the relevant resources and initiate the redeployment to the environment. Please confirm to proceed.`,
        },
        isDisabled: !(
          initialData?.status === ResourceStatus.CreateError ||
          initialData?.status === ResourceStatus.DeleteError ||
          initialData?.status === ResourceStatus.Ready ||
          initialData?.status === ResourceStatus.DeleteCompleted
        ),
        onActionSelected: async () => {
          await envApolloClient.mutate<
            ReDeploymentServiceMutation,
            ReDeploymentServiceMutationVariables
          >({
            mutation: ReDeploymentServiceDocument,
            variables: {
              input: {
                serviceDeploymentId: serviceDeploymentId,
              },
            },
            fetchPolicy: 'no-cache',
          });

          history.replace(history.location.pathname);
        },
      },
      {
        label: 'Undeploy',
        confirmationMode: 'Advanced',
        confirmationConfig: {
          body: 'The action is irreversible. By confirming the action the service deployment and its related data will be purged. Please confirm to proceed.',
        },
        isDisabled: !(
          initialData?.status === ResourceStatus.CreateError ||
          initialData?.status === ResourceStatus.Ready ||
          initialData?.status === ResourceStatus.DeleteError
        ),
        onActionSelected: async () => {
          await envApolloClient.mutate<
            UnDeploymentServiceMutation,
            UnDeploymentServiceMutationVariables
          >({
            mutation: UnDeploymentServiceDocument,
            variables: {
              input: {
                serviceDeploymentId: serviceDeploymentId,
              },
            },
            fetchPolicy: 'no-cache',
          });
          history.replace(history.location.pathname);
        },
      },
    );
  }

  return { actions } as const;
}

export const CustomizableServiceDeploymentDetailsCrumb: BreadcrumbResolver =
  () => 'Deployments Details';
